import React, { useState, useEffect } from 'react';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { IClientInfo } from '../../lib/types';
import { LanguageSwitcher } from '../shared/LanguageSwitcher';
import { ThemeSelector } from '../shared/ThemeSelector';
import { AppBranding } from '../shared/AppBranding';
import { createAnonymousRoom } from '../../lib/api-client';
import { generateRoomId } from '../../lib/client-utils';
import { ROUTES } from '../../lib/routes';
import { fetchAppConfig } from '../../lib/app-config';
import { useRuntimeConfig } from '../../lib/runtime-config';
import styles from '../../styles/Home.module.css';

interface AnonymousHomePageProps {
  themeConfig: IClientInfo;
  translations: any;
}

export const AnonymousHomePage: React.FC<AnonymousHomePageProps> = ({
  themeConfig,
  translations
}) => {
  const t = translations.home;
  const router = useRouter();
  const { livekitProjectId } = useRuntimeConfig();
  const [isCreating, setIsCreating] = useState(false);
  const [emailRegistrationEnabled, setEmailRegistrationEnabled] = useState(false);

  useEffect(() => {
    fetchAppConfig().then((config) => {
      setEmailRegistrationEnabled(config.emailRegistrationEnabled);
    });
  }, []);

  const handleCreateMeeting = async () => {
    if (isCreating) return;

    try {
      setIsCreating(true);
      const roomName = generateRoomId(livekitProjectId);
      const response = await createAnonymousRoom(roomName);

      // Navigate to the new room
      await router.push(ROUTES.ROOMS(response.data.room_name));
    } catch (error) {
      console.error('Failed to create room:', error);
      alert(t.errorCreatingRoom || 'Failed to create room. Please try again.');
    } finally {
      setIsCreating(false);
    }
  };

  const handleSignIn = () => {
    router.push(ROUTES.SIGNIN);
  };

  const handleSignUp = () => {
    router.push(ROUTES.SIGNUP);
  };

  return (
    <>
      <Head>
        <title>{themeConfig.title}</title>
        {themeConfig && <link rel="icon" href={themeConfig.favicon} />}
        {/* Pre-inject CSS variables to prevent flashing */}
        <style>
          {`
            :root {
              --brand-color: ${themeConfig.colors.brand};
              --background-color: ${themeConfig.colors.background};
              --surface-color: ${themeConfig.colors.surface};
              --placeholder-color: ${themeConfig.colors.placeholder};
            }

            /* Smooth transitions for theme switching */
            * {
              transition: background-color 0.3s ease, color 0.3s ease, border-color 0.3s ease;
            }

            /* Smooth transitions for images */
            .theme-image {
              transition: opacity 0.3s ease, transform 0.3s ease;
            }

            .theme-image.switching {
              opacity: 0.8;
              transform: scale(0.98);
            }

            /* Smooth transitions for text content */
            h1, h2, h3, h4, h5, h6, p, span {
              transition: color 0.3s ease, opacity 0.3s ease;
            }

            /* Smooth transitions for buttons */
            button {
              transition: background-color 0.3s ease, color 0.3s ease, border-color 0.3s ease, transform 0.1s ease;
            }

            button:hover {
              transform: translateY(-1px);
            }

            button:active {
              transform: translateY(0);
            }
          `}
        </style>
      </Head>
      <main className={styles.main} data-lk-theme="default">
        <div className={styles.languageSwitcherContainer}>
          <LanguageSwitcher />
          <ThemeSelector />
        </div>
        <div className={styles.header}>
          <AppBranding
            themeConfig={themeConfig}
            translations={translations}
          />
          <button
            className={styles.buttonCreateMeeting}
            onClick={handleCreateMeeting}
            disabled={isCreating}
          >
            {isCreating ? t.creatingMeeting : t.title}
          </button>
          <button className={styles.buttonSignIn} onClick={handleSignIn}>
            {t.signInButton}
          </button>
          {emailRegistrationEnabled && (
            <button className={styles.buttonSignUp} onClick={handleSignUp}>
              {t.signUpButton}
            </button>
          )}
        </div>
      </main>
    </>
  );
};
